/**
 * 1stbuddha — search.js
 *
 * <search-page> (sections/search.liquid)
 *   - clear button empties the field, hides itself and refocuses the field
 *   - collections: the storefront search doesn't return collections, so they
 *     are fetched from the Predictive Search API (/search/suggest.json) and
 *     rendered from the <template>; the "Collections" link and count appear
 *     when there are any
 *   - result-type links follow the scroll position (aria-current)
 */
(() => {
  if (customElements.get('search-page')) return;

  const parser = new DOMParser();

  class SearchPage extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.input = this.querySelector('[data-search-input]');
      this.clear = this.querySelector('[data-search-clear]');
      this.onClick = this.onClick.bind(this);

      this.addEventListener('click', this.onClick);
      this.input?.addEventListener('input', () => {
        if (this.clear) this.clear.hidden = this.input.value === '';
      });
      this.clear?.addEventListener('click', () => {
        this.input.value = '';
        this.clear.hidden = true;
        this.input.focus();
      });

      if (this.dataset.terms) this.loadCollections();
      this.spy();
      this.observeLoadMore();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.loadMoreObserver?.disconnect();
    }

    onClick(event) {
      const more = event.target.closest('[data-load-more]');
      if (more && this.contains(more)) {
        event.preventDefault();
        this.loadMore(more, { automatic: false });
      }
    }

    observeLoadMore() {
      this.loadMoreObserver?.disconnect();
      const link = this.querySelector('[data-load-more]');
      if (!link || !('IntersectionObserver' in window)) return;

      this.loadMoreObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry?.isIntersecting) {
            this.loadMore(link, { automatic: true });
          }
        },
        { rootMargin: '400px 0px 400px 0px' }
      );
      this.loadMoreObserver.observe(link);
    }

    async loadMore(link, { automatic = false } = {}) {
      if (!link || link.getAttribute('aria-busy') === 'true' || this.isLoadingMore) return;
      this.isLoadingMore = true;
      link.setAttribute('aria-busy', 'true');
      const url = new URL(link.href, window.location.href);
      const params = new URLSearchParams(url.search);
      if (this.sectionId) params.set('section_id', this.sectionId);

      try {
        const response = await fetch(`${url.pathname}?${params.toString()}`);
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        const html = await response.text();
        const next = parser.parseFromString(html, 'text/html');

        const grid = this.querySelector('[data-search-grid]');
        const items = [...next.querySelectorAll('[data-search-grid] > [data-search-item]')];
        if (grid && items.length > 0) {
          grid.append(...items);
          window.themeWishlist?.sync(grid);
        }

        const articlesList = this.querySelector('.search__articles');
        const newArticles = [...next.querySelectorAll('.search__articles > li')];
        if (articlesList && newArticles.length > 0) {
          articlesList.append(...newArticles);
        }

        const region = this.querySelector('[data-search-region="load-more"]');
        const fresh = next.querySelector('[data-search-region="load-more"]');
        if (region && fresh) region.replaceWith(fresh);
        else region?.remove();

        url.searchParams.delete('section_id');
        window.history.replaceState({ search: true }, '', url.toString());
        this.spy();
      } catch (error) {
        if (error.name !== 'AbortError' && !automatic) {
          window.location.assign(link.href);
        }
      } finally {
        link.removeAttribute('aria-busy');
        this.isLoadingMore = false;
        this.observeLoadMore();
      }
    }

    async loadCollections() {
      const section = this.querySelector('[data-collections]');
      const list = this.querySelector('[data-collections-list]');
      const template = this.querySelector('[data-collection-template]');
      if (!section || !list || !template) return;

      const params = new URLSearchParams({
        q: this.dataset.terms,
        'resources[type]': 'collection',
        'resources[limit]': '6',
      });
      try {
        const response = await fetch(`${this.dataset.suggestUrl}.json?${params}`);
        if (!response.ok) return;
        const data = await response.json();
        const collections = data?.resources?.results?.collections || [];
        if (!collections.length) return;

        collections.forEach((collection) => {
          const item = template.content.firstElementChild.cloneNode(true);
          const link = item.querySelector('a');
          link.href = collection.url;
          item.querySelector('.search-collection__title').textContent = collection.title.split(' — ')[0];
          const imageUrl = collection.featured_image?.url || collection.image;
          if (imageUrl) {
            const image = document.createElement('img');
            image.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=200`;
            image.alt = collection.featured_image?.alt || collection.title;
            image.loading = 'lazy';
            image.width = 200;
            image.height = 200;
            item.querySelector('.search-collection__media').append(image);
          }
          list.append(item);
        });

        section.hidden = false;
        const tab = this.querySelector('[data-collections-tab]');
        const count = this.querySelector('[data-collections-count]');
        if (count) count.textContent = String(collections.length);
        if (tab) tab.hidden = false;
        this.spy();
      } catch {
        /* collections are a bonus; the page works without them */
      }
    }

    spy() {
      this.observer?.disconnect();
      const tabs = [...this.querySelectorAll('[data-search-tabs] a:not([hidden])')];
      const panels = tabs.map((tab) => this.querySelector(tab.getAttribute('href'))).filter(Boolean);
      if (!panels.length || !('IntersectionObserver' in window)) return;

      const setCurrent = (id) =>
        tabs.forEach((tab) => {
          if (tab.getAttribute('href') === `#${id}`) tab.setAttribute('aria-current', 'true');
          else tab.removeAttribute('aria-current');
        });

      this.observer = new IntersectionObserver(
        (entries) => {
          const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) setCurrent(visible[0].target.id);
        },
        { rootMargin: '-30% 0px -60% 0px' }
      );
      panels.forEach((panel) => this.observer.observe(panel));
    }
  }

  customElements.define('search-page', SearchPage);
})();

